import React, {
  createContext,
  PropsWithChildren,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";
import { notification } from "antd";
import { postLogin } from "../services/auth";
import { authBus, MotivoFimSessao } from "../services/authBus";
import { sessionManager } from "../services/sessionManager";
import { CHAVES, SESSAO, storage } from "../services/sessionConfig";
import { getSessionExpiryMs } from "../utils/jwt";

interface AuthContextData {
  user?: any;
  signed?: boolean;
  loading?: boolean;
  loadingSignIn?: boolean;
  /** Instante em que a sessão será encerrada por inatividade, ou null. */
  avisoInatividadeAte?: number | null;
  signIn(
    email: string,
    password: string
  ): Promise<void>;
  signOut: (motivo?: MotivoFimSessao) => void;
  setAuthUser: (userData: any) => void;
  continuarSessao: () => Promise<void>;
}

export const AuthContext = createContext<AuthContextData>(
  {} as AuthContextData
);

const MENSAGEM_FIM_SESSAO: Record<MotivoFimSessao, string | null> = {
  expirada: "Sua sessão expirou. Faça login novamente.",
  inatividade: "Sessão encerrada por inatividade.",
  // Logout disparado em outra aba: o usuário já viu o que aconteceu lá.
  "outra-aba": null,
};

/** Sessão inteiramente encerrada: some do storage e para os timers. */
const limparSessaoLocal = () => {
  sessionManager.parar();
  storage.remover(CHAVES.USER);
  storage.remover(CHAVES.UNIDADE);
  storage.remover(CHAVES.ULTIMA_ATIVIDADE);
  storage.remover(CHAVES.RENOVACAO_LOCK);
};

export const AuthProvider: React.FC<PropsWithChildren<unknown>> = ({
  children,
}) => {
  const [user, setUser] = useState<any>(null);
  const [signed, setSigned] = useState<boolean>(false);
  const [loadingSignIn, setLoadingSignIn] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(true);
  const [avisoInatividadeAte, setAvisoInatividadeAte] = useState<number | null>(
    null
  );

  const signIn = useCallback(async (email: string, password: string) => {
    const body: any = {
      email,
      password,
    };
    setLoadingSignIn(true);
    try {
      const response = await postLogin(body);
      if (response.status === 200) {
        storage.gravar(CHAVES.USER, JSON.stringify(response.data));
        authBus.resetar();
        sessionManager.registrarAtividade(true);
      }
    } finally {
      setLoadingSignIn(false);
      setLoading(false);
    }
  }, []);

  const signOut = useCallback((motivo?: MotivoFimSessao) => {
    // Guarda a rota atual para restaurá-la após o relogin. Só faz sentido em
    // encerramento involuntário — no "Sair" manual o usuário quer sair mesmo.
    // O AuthProvider está fora do BrowserRouter, então a rota vem do window.
    // O teste de tipo protege contra `onClick={signOut}`, que passaria o evento
    // de clique como motivo.
    if (typeof motivo === "string") {
      const rota = window.location.pathname + window.location.search;
      if (rota && rota !== "/") {
        try {
          sessionStorage.setItem(CHAVES.REDIRECT, rota);
        } catch {
          // Sem sessionStorage o relogin apenas cai na home.
        }
      }
    }

    limparSessaoLocal();
    setUser(null);
    setSigned(false);
    setAvisoInatividadeAte(null);
    // Sem window.location.href aqui: com signed = false o Routes já troca para
    // as rotas públicas, e um reload apagaria a notificação recém-exibida.
  }, []);

  const setAuthUser = useCallback((userData: any) => {
    setUser(userData);
    setSigned(true);
  }, []);

  const continuarSessao = useCallback(async () => {
    setAvisoInatividadeAte(null);
    await sessionManager.continuarSessao();
  }, []);

  // Restauração da sessão no boot.
  useEffect(() => {
    const userStorage = storage.lerJson(CHAVES.USER);
    if (userStorage) {
      const expiraEm = getSessionExpiryMs(userStorage);
      // Expiração desconhecida (null) é fail-open: mantém a sessão e deixa o
      // 401 da API decidir. Só descarta o que sabidamente já venceu.
      if (expiraEm !== null && expiraEm - SESSAO.CLOCK_SKEW_MS <= Date.now()) {
        limparSessaoLocal();
      } else {
        setUser(userStorage);
        setSigned(true);
      }
    }
    // Sempre — antes, o loading ficava preso em true quando não havia usuário.
    setLoading(false);
  }, []);

  // Eventos publicados pelo requestApi e pelo sessionManager (ambos fora do React).
  useEffect(
    () =>
      authBus.subscribe((evento) => {
        switch (evento.tipo) {
          case "sessao-renovada":
            setUser(evento.user);
            break;
          case "aviso-inatividade":
            setAvisoInatividadeAte(evento.expiraEm);
            break;
          case "sessao-expirada": {
            const mensagem = MENSAGEM_FIM_SESSAO[evento.motivo];
            if (mensagem) {
              notification.warning({
                message: "Sessão encerrada",
                description: mensagem,
                placement: "topRight",
                duration: 8,
              });
            }
            signOut(evento.motivo);
            break;
          }
        }
      }),
    [signOut]
  );

  // O controle de sessão só roda com usuário logado.
  useEffect(() => {
    if (!signed) return;
    return sessionManager.iniciar();
  }, [signed]);

  const props = useMemo(
    () => ({
      signed,
      user,
      signIn,
      signOut,
      loading,
      loadingSignIn,
      setAuthUser,
      avisoInatividadeAte,
      continuarSessao,
    }),
    [
      user,
      signed,
      signIn,
      signOut,
      loading,
      loadingSignIn,
      setAuthUser,
      avisoInatividadeAte,
      continuarSessao,
    ]
  );

  return <AuthContext.Provider value={props}>{children}</AuthContext.Provider>;
};
